import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Save, X, Loader2, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

const emptyService = {
  title: '',
  description: '',
  icon: 'code',
  features: [],
  order: 0,
  active: true,
};

const ServicesManager = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [featuresText, setFeaturesText] = useState('');

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await adminApi.getServices();
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setFeaturesText(service.features?.join('\n') || '');
    } else {
      setEditingService({ ...emptyService, order: services.length });
      setFeaturesText('');
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingService(null);
    setFeaturesText('');
  };

  const handleChange = (e) => {
    setEditingService({ ...editingService, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const serviceData = {
        ...editingService,
        features: featuresText.split('\n').filter(f => f.trim()),
      };

      if (editingService.id) {
        await adminApi.updateService(editingService.id, serviceData);
        toast.success('Service updated successfully');
      } else {
        await adminApi.createService(serviceData);
        toast.success('Service created successfully');
      }
      fetchServices();
      closeDialog();
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await adminApi.deleteService(id);
      toast.success('Service deleted');
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="services-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-heading font-bold mb-2">Services</h1>
          <p className="text-muted-foreground">Manage your service offerings.</p>
        </div>
        <Button onClick={() => openDialog()} className="btn-primary" data-testid="add-service-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Service
        </Button>
      </div>

      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Title</th>
              <th>Icon</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {services.map((service) => (
              <tr key={service.id} data-testid={`service-row-${service.id}`}>
                <td>
                  <GripVertical className="w-4 h-4 text-muted-foreground" />
                </td>
                <td className="font-medium">{service.title}</td>
                <td className="text-muted-foreground">{service.icon}</td>
                <td>
                  <span className={service.active ? 'badge-success' : 'badge-error'}>
                    {service.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDialog(service)}
                      data-testid={`edit-service-${service.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(service.id)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`delete-service-${service.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {services.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-muted-foreground py-8">
                  No services yet. Add your first service.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0f172a] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingService?.id ? 'Edit Service' : 'Add Service'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">Title</label>
              <Input
                name="title"
                value={editingService?.title || ''}
                onChange={handleChange}
                placeholder="Web Development"
                className="input-neon"
                data-testid="service-title-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <Textarea
                name="description"
                value={editingService?.description || ''}
                onChange={handleChange}
                placeholder="Custom websites built with modern technologies"
                rows={3}
                className="input-neon resize-none"
                data-testid="service-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Icon Name</label>
                <Input
                  name="icon"
                  value={editingService?.icon || ''}
                  onChange={handleChange}
                  placeholder="code"
                  className="input-neon"
                  data-testid="service-icon-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Order</label>
                <Input
                  name="order"
                  type="number"
                  value={editingService?.order || 0}
                  onChange={handleChange}
                  className="input-neon"
                  data-testid="service-order-input"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Features (one per line)</label>
              <Textarea
                value={featuresText}
                onChange={(e) => setFeaturesText(e.target.value)}
                placeholder="React/Next.js&#10;Node.js/Python&#10;MongoDB/PostgreSQL"
                rows={4}
                className="input-neon resize-none"
                data-testid="service-features-input"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="form-label">Active</label>
              <Switch
                checked={editingService?.active ?? true}
                onCheckedChange={(checked) => setEditingService({ ...editingService, active: checked })}
                data-testid="service-active-switch"
              />
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button variant="ghost" onClick={closeDialog} data-testid="cancel-service-btn">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-service-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesManager;
